import { useState } from 'react';
import type { CharacterBenefits } from '../../hooks/useCharacterBenefits';
import type { PlaySession, CustomEffect } from '../../stores/playSessionStore';
import type { ConditionName } from '@character-creator/types';
import { getCondition } from '@character-creator/data';
import { getBardicInspirationDie } from '@character-creator/core';
import { BardicInspirationIcon, DmInspirationIcon, CustomEffectIcon } from './PlayModeIcons';
import { formatConditionName } from './sheet/constants';

interface ActiveEffectsSectionProps {
  session: PlaySession | null;
  benefits: CharacterBenefits;
  onToggleCondition: (condition: ConditionName) => void;
  onToggleBardicInspiration: () => void;
  onToggleDmInspiration: () => void;
  onRemoveCustomEffect: (id: string) => void;
}

export function ActiveEffectsSection({
  session,
  benefits,
  onToggleCondition,
  onToggleBardicInspiration,
  onToggleDmInspiration,
  onRemoveCustomEffect,
}: ActiveEffectsSectionProps) {
  const [expandedCondition, setExpandedCondition] = useState<string | null>(null);
  const [expandedEffect, setExpandedEffect] = useState<string | null>(null);

  const conditions = session?.activeConditions ?? [];
  const bardicInspiration = session?.bardicInspiration ?? false;
  const dmInspiration = session?.dmInspiration ?? false;
  const customEffects: CustomEffect[] = session?.customEffects ?? [];

  const hasAnyEffects =
    conditions.length > 0 ||
    bardicInspiration ||
    dmInspiration ||
    customEffects.length > 0 ||
    (session?.exhaustionLevel ?? 0) > 0;

  if (!hasAnyEffects) return null;

  const bardLevel = benefits.classLevels.find((cl) => cl.classId === 'bard')?.level ?? 0;

  return (
    <div className="bg-background-card border border-warning/30 rounded-lg overflow-hidden">
      <div className="bg-warning/10 px-3 py-2 font-display text-xs text-warning uppercase tracking-wider">
        Active Effects
      </div>
      <div className="p-2 space-y-1">
        {/* Conditions */}
        {conditions.map((condition) => {
          const conditionData = getCondition(condition);
          const isExpanded = expandedCondition === condition;
          return (
            <div key={condition}>
              <div className="flex items-center gap-2 px-2 py-1.5 bg-error/10 border border-error/30 rounded">
                <span className="text-xs text-error font-medium flex-1">
                  {formatConditionName(condition)}
                </span>
                {conditionData && (
                  <button
                    onClick={() => setExpandedCondition(isExpanded ? null : condition)}
                    className="w-4 h-4 flex items-center justify-center text-[10px] text-error/60
                      hover:text-error rounded-full border border-error/30 hover:bg-error/10 transition-colors"
                    title="Show details"
                  >
                    i
                  </button>
                )}
                <button
                  onClick={() => onToggleCondition(condition)}
                  className="text-error/60 hover:text-error text-xs transition-colors"
                  title="Remove"
                >
                  ×
                </button>
              </div>
              {isExpanded && conditionData && (
                <div className="mx-2 mt-1 mb-1 px-2 py-1.5 bg-error/5 border-l-2 border-error/30 rounded-r text-xs">
                  <p className="text-text-secondary mb-1">{conditionData.description}</p>
                  <ul className="list-disc list-inside text-text-muted space-y-0.5">
                    {conditionData.effects.map((effect, i) => (
                      <li key={i}>{effect}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}

        {/* Exhaustion */}
        {(session?.exhaustionLevel ?? 0) > 0 && (
          <div className="flex items-center gap-2 px-2 py-1.5 bg-error/10 border border-error/30 rounded">
            <span className="text-xs text-error font-medium flex-1">
              Exhaustion Level {session?.exhaustionLevel}
            </span>
          </div>
        )}

        {/* Inspirations */}
        {bardicInspiration && (
          <div className="flex items-center gap-2 px-2 py-1.5 bg-accent-gold/10 border border-accent-gold/30 rounded">
            <BardicInspirationIcon className="w-3.5 h-3.5 text-accent-gold" />
            <span className="text-xs text-accent-gold font-medium flex-1">
              Bardic Inspiration{bardLevel > 0 ? ` (${getBardicInspirationDie(bardLevel)})` : ''}
            </span>
            <button
              onClick={onToggleBardicInspiration}
              className="text-accent-gold/60 hover:text-accent-gold text-xs transition-colors"
              title="Remove"
            >
              ×
            </button>
          </div>
        )}
        {dmInspiration && (
          <div className="flex items-center gap-2 px-2 py-1.5 bg-accent-gold/10 border border-accent-gold/30 rounded">
            <DmInspirationIcon className="w-3.5 h-3.5 text-accent-gold" />
            <span className="text-xs text-accent-gold font-medium flex-1">Inspired</span>
            <button
              onClick={onToggleDmInspiration}
              className="text-accent-gold/60 hover:text-accent-gold text-xs transition-colors"
              title="Remove"
            >
              ×
            </button>
          </div>
        )}

        {/* Custom Effects */}
        {customEffects.map((effect) => {
          const isExpanded = expandedEffect === effect.id;
          return (
            <div key={effect.id}>
              <div className="flex items-center gap-2 px-2 py-1.5 bg-class-wizard/10 border border-class-wizard/30 rounded">
                <CustomEffectIcon className="w-3.5 h-3.5 text-class-wizard" />
                <button
                  onClick={() => effect.description && setExpandedEffect(isExpanded ? null : effect.id)}
                  className="text-xs text-class-wizard font-medium flex-1 text-left"
                >
                  {effect.name}
                </button>
                <button
                  onClick={() => onRemoveCustomEffect(effect.id)}
                  className="text-class-wizard/60 hover:text-class-wizard text-xs transition-colors"
                  title="Remove"
                >
                  ×
                </button>
              </div>
              {isExpanded && effect.description && (
                <div className="mx-2 mt-1 mb-1 px-2 py-1.5 bg-class-wizard/5 border-l-2 border-class-wizard/30 rounded-r text-xs text-text-muted">
                  {effect.description}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
